import { useEffect, useState, type ReactNode } from 'react'

// ─── data ────────────────────────────────────────────────────────────────────

const REPORT_META = {
  date: 'August 7, 2026',
  series: 'Overlooked Stack — III of IV',
  audience: 'Multi-month / Long-term Allocators',
  sector: 'AI Data-Center Liquid Cooling / Thermal Management',
  companions: [
    { label: 'Power Scarcity Overlooked Stack', date: 'Jul 30' },
    { label: 'Electrical Last Mile Overlooked Stack', date: 'Aug 5' },
    { label: 'Receipt Regime Synergy — Three Large Caps', date: 'Aug 6' },
  ],
}

const NAMES = [
  { ticker: 'MOD', name: 'Modine Manufacturing', price: '$195.60', role: 'Torque', rsi: 38, vsSma: '−10%', macd: 'Negative', structure: 'Early base', stop: '<$178', confirm: '>$218 → $227 fib', color: '#8b6914' },
  { ticker: 'AAON', name: 'AAON Inc.', price: '$94.83', role: 'Discovery', rsi: 41, vsSma: '−6%', macd: 'Flipped +', structure: 'Base + first turn', stop: '<$85', confirm: '>$101 → $109', color: '#2a6080' },
  { ticker: 'ECL', name: 'Ecolab Inc.', price: '$285.17', role: 'Ballast', rsi: 61, vsSma: '+4%', macd: 'Positive', structure: 'Uptrend', stop: '<$269', confirm: 'Hold >$277; >$288', color: '#2e7d4f' },
]

const MACRO_TABLE = [
  { signal: 'Nonfarm Payrolls', print: '−23k vs +80k expected', implication: 'Dovish bid → growth / software crowded' },
  { signal: 'Nasdaq / XLK', print: '+1.30% / +1.42%', implication: 'First-order AI ownership re-owned' },
  { signal: 'XLI (Industrials)', print: '+0.23%', implication: 'Physical delivery still orphaned' },
  { signal: 'USO (5-day)', print: '−3.39%', implication: 'Oil not the primary clock' },
  { signal: '10y Yield / VIX', print: '4.65% / 14.9', implication: 'Risk-on, low fear — no panic bid into industrials' },
]

const WASH_TABLE = [
  { name: 'MOD', wash: '−26.4%', vsJul24: '−19.1%', excessSpy: '−23.7 pt', vsJunePeak: '−36%', highlight: true },
  { name: 'AAON', wash: '−17.3%', vsJul24: '−7.6%', excessSpy: '−12.3 pt', vsJunePeak: '−36%', highlight: true },
  { name: 'SPY', wash: '−1.3%', vsJul24: '+4.6%', excessSpy: '—', vsJunePeak: '—', highlight: false },
  { name: 'ECL', wash: '+5.6%', vsJul24: '+6.1%', excessSpy: '+1.5 pt', vsJunePeak: '~flat', highlight: false },
]

const ANALOG_TABLE = [
  { episode: 'May 20 \'26 — Oil Relief (+10d)', mod: '+14.9 pt', aaon: '+5.3 pt', ecl: '+0.2 pt', vrt: '+0.5 pt' },
  { episode: 'Apr 27 \'26 — AI/DC Receipt (+20d)', mod: '+15.8 pt', aaon: '+49.0 pt', ecl: '−10.0 pt', vrt: '−4.5 pt' },
  { episode: 'Jul 31 \'25 — Twin (+60d)', mod: '+12.7 pt', aaon: '+20.1 pt', ecl: '−1.2 pt', vrt: '+20.6 pt' },
  { episode: 'Aug 7 FO Similar Days (+20d, n=12)', mod: '+13.7 pt · 75%', aaon: '+3.2 pt · 75%', ecl: '+2.1 pt · 75%', vrt: '+6.9 pt · 67%' },
]

const BOOK_TABLE = [
  { role: 'Torque', name: 'MOD', weight: 'Smaller — ATR ~8%', addWhen: 'Close >~$218', stop: '<$178' },
  { role: 'Discovery', name: 'AAON', weight: 'Core growth sleeve', addWhen: 'Close >~$101', stop: '<$85' },
  { role: 'Ballast', name: 'ECL', weight: 'Quality duration', addWhen: 'Dips to $275–277', stop: '<$269' },
]

const SYNTHESIS_TABLE = [
  { layer: 'Industry', finding: 'Heat is the next unpaid AI gate', decision: 'Own thermal, not another GPU proxy' },
  { layer: 'Regime', finding: 'Aug 7 crowded software; XLI orphaned', decision: 'Buy the lag, don\'t chase soft-SaaS re-rate' },
  { layer: 'Wash', finding: 'Jul 24–29 climax selling', decision: 'Entry asymmetry still live for MOD / AAON' },
  { layer: 'Analogs', finding: 'May 20 / Apr 27 / FO +20d pay this sleeve', decision: 'Multi-month horizon, 75% hit on twins' },
  { layer: 'Technicals', finding: 'Two bases + one uptrend', decision: 'Accumulate repair; confirm on SMA20' },
  { layer: 'Fundamentals', finding: 'LTA / backlog / CoolIT already executed', decision: 'Fundamentals lead; price lags on torque names' },
  { layer: 'History', finding: 'Vertiv digestion; Nalco attach', decision: 'Great returns required holding the ugly middle' },
]

const KILL_LIST = [
  'Hyperscaler capex cuts that hit cooling orders, not just soft SaaS guides',
  'MOD LTA cancellation or AAON BASX book-to-bill collapse',
  'CoolIT attach rates fail — hardware without chemistry recurring revenue',
  'True break of wash lows on MOD/AAON with rising volume (thesis break, not noise)',
  'Air cooling somehow solves 200kW+ racks (physics says no; watch anyway)',
]

// ─── sub-components ──────────────────────────────────────────────────────────

function Label({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9a9590' }}>
      {children}
    </span>
  )
}

function SectionTitle({ number, title }: { number: string; title: string }) {
  return (
    <div style={{ borderTop: '1px solid #dedad4', paddingTop: '2rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#c8c3bc', minWidth: '2rem' }}>{number}</span>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.6rem', fontWeight: 400, color: '#1a1c20', margin: 0, letterSpacing: '-0.01em' }}>{title}</h2>
      </div>
    </div>
  )
}

function Table({ headers, rows, highlightRow }: { headers: string[]; rows: (string | ReactNode)[][]; highlightRow?: (i: number) => boolean }) {
  return (
    <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '8px 12px', color: '#9a9590', fontWeight: 500, letterSpacing: '0.08em', fontSize: '10px', textTransform: 'uppercase', borderBottom: '1px solid #dedad4', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ backgroundColor: highlightRow?.(i) ? 'rgba(139,105,20,0.05)' : 'transparent', borderBottom: '1px solid #edeae5' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '9px 12px', color: '#3a3c42', verticalAlign: 'top', lineHeight: 1.4 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Pill({ label, color = '#8b6914' }: { label: string; color?: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', border: `1px solid ${color}40`, backgroundColor: `${color}0d`, color, borderRadius: '2px', fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
      {label}
    </span>
  )
}

function NameCard({ ticker, name, price, role, rsi, vsSma, macd, structure, stop, confirm, color }: typeof NAMES[0]) {
  const [expanded, setExpanded] = useState(false)

  const details: Record<string, { fuel: string[]; rhyme: string; falsify: string }> = {
    MOD: {
      fuel: [
        '$4B long-term capacity agreement for 2027–2029 with strategic hyperscale customer; $165M upfront to fund the build',
        'FY26: Data Centers crossed ~$1.1B (+73%); Q4 DC sales +158%',
        'Pending PT spin into Gentherm — leaves focused climate/DC company',
        'Largest capacity expansion in company history (Calgary, Dallas, Grenada et al.)',
      ],
      rhyme: 'Vertiv: legacy precision cooling → public → −44% in 2022 → AI densification recognition → roughly +1,950% from early 2023 to Aug 2026. MOD already compounded hard (+2,400% since 2020) but is −34% since the May 26 LTA day. Receipt-then-wash, not thesis break.',
      falsify: 'LTA cancel or slippage; DC growth rolls over; margins never recover post-ramp; close loses $178 wash low.',
    },
    AAON: {
      fuel: [
        'Q1\'26: sales +54%; total backlog $2.13B (+107%); BASX backlog $1.62B (+160%), book-to-bill >2',
        'Guide raised to 40–45% 2026 sales growth (GM 27–28% while ramping)',
        '~$1B BASX revenue implied for 2026; management cites capacity potential above $2B',
        'Liquid cooling TTM sales +187%; TAM view shifts toward ~60% liquid / 40% air by 2028',
        '+1M+ sq ft capacity since 2024; Memphis DC plant online; $190M 2026 capex',
      ],
      rhyme: 'Quality HVAC compounders leaning into mission-critical/DC (Trane/Eaton-class ~+180% in the AI era) show the slower path: backlog → capacity → mix → multiple. AAON\'s Apr 27 archive path (+49pt / 20d) is the high-beta version of that recognition. −27% since the May 7 guide raise is capacity-cost digestion while backlog still rises.',
      falsify: 'BASX book-to-bill rolls; Memphis/Longview ramp fails; May \'25-style path fade repeats without DC mix offset; close loses ~$85.',
    },
    ECL: {
      fuel: [
        'CoolIT acquired for $4.75B, closed July 2 (early); ~$550M NTM sales at announce; YTD growth >100% at close',
        'High-Tech: ~$150M (2021) → ~$1.5B annualized (2026) → target $4B by 2030 at ~25% OI margins',
        'Prior brick already laid: Ovivo ultrapure water (Dec \'25)',
        'Near-term EPS diluted by amortization/financing; accretion expected 2028; Nalco amortization rolls off 2027',
      ],
      rhyme: 'Ecolab\'s own Nalco playbook: the last transformative control-point buy produced roughly +109% over the subsequent five-year window as attach scaled — not a one-day spike. CoolIT is the AI-thermal sequel. Only +11% since announce — early versus that multi-year arc. Vertiv\'s PurgeRite fluid deal validates the control-point logic from the OEM side.',
      falsify: 'CoolIT integration fails; hyperscalers reject chemistry attach; leverage stays elevated; sustained loss of ~$269.',
    },
  }

  const d = details[ticker]

  return (
    <div style={{ border: '1px solid #dedad4', backgroundColor: '#faf8f5', marginBottom: '1rem' }}>
      <div
        style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{ minWidth: '56px' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '18px', fontWeight: 600, color }}>{ticker}</div>
          <Pill label={role} color={color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.05rem', color: '#2a2c30', fontWeight: 400 }}>{name}</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: '#9a9590', marginTop: '2px' }}>{price} · RSI {rsi} · {vsSma} vs SMA20 · MACD {macd}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#b8b3ac', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>Structure</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#6a6d75' }}>{structure}</div>
        </div>
        <div style={{ color: '#c8c3bc', fontSize: '16px', marginLeft: '1rem' }}>{expanded ? '▲' : '▼'}</div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #dedad4', padding: '1.25rem 1.5rem', backgroundColor: '#f5f3ef' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <div style={{ marginBottom: '0.75rem' }}><Label>Company Fuel (Now)</Label></div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {d.fuel.map((f, i) => (
                  <li key={i} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.6rem', color: '#4a4c52', fontSize: '13px', lineHeight: 1.5 }}>
                    <span style={{ color, flexShrink: 0, marginTop: '2px', fontFamily: "'JetBrains Mono', monospace", fontSize: '10px' }}>→</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div style={{ marginBottom: '0.75rem' }}><Label>Historical Rhyme</Label></div>
              <p style={{ color: '#4a4c52', fontSize: '13px', lineHeight: 1.6, margin: '0 0 1.25rem' }}>{d.rhyme}</p>
              <div style={{ marginBottom: '0.5rem' }}><Label>Invalidation</Label></div>
              <p style={{ color: '#8b3028', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5, margin: 0, backgroundColor: 'rgba(180,60,48,0.04)', padding: '0.75rem', border: '1px solid rgba(180,60,48,0.15)' }}>{d.falsify}</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e8e4de' }}>
            <div>
              <Label>Add / Confirm Signal</Label>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: '#2e7d4f', marginTop: '6px' }}>{confirm}</div>
            </div>
            <div>
              <Label>Stop / Invalidation Level</Label>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: '#b84038', marginTop: '6px' }}>{stop}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── main ────────────────────────────────────────────────────────────────────

const ARTICLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;1,9..40,400&family=DM+Serif+Display&family=JetBrains+Mono:wght@400;500;600&display=swap'

function useArticleFonts() {
  useEffect(() => {
    const existing = document.getElementById('unpaid-layer-article-fonts')
    if (existing) return

    const link = document.createElement('link')
    link.id = 'unpaid-layer-article-fonts'
    link.rel = 'stylesheet'
    link.href = ARTICLE_FONTS_HREF
    document.head.appendChild(link)
  }, [])
}

export function UnpaidLayerLiquidCoolingArticle() {
  useArticleFonts()

  return (
    <div style={{ backgroundColor: '#f5f3ef', minHeight: '100vh' }}>

      {/* ── header bar ── */}
      <div style={{ backgroundColor: '#f5f3ef', borderBottom: '1px solid #dedad4', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '44px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.2em', color: '#8b6914', textTransform: 'uppercase' }}>Equity Research</span>
            <span style={{ color: '#dedad4' }}>|</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#9a9590' }}>OVERLOOKED STACK — III OF IV</span>
          </div>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#9a9590' }}>{REPORT_META.date}</span>
        </div>
      </div>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '3rem 2rem 5rem' }}>

        {/* ── title block ── */}
        <div style={{ marginBottom: '3rem', paddingBottom: '2.5rem', borderBottom: '1px solid #dedad4' }}>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Pill label="AI Data-Center" color="#2a6080" />
            <Pill label="Liquid Cooling / Thermal" color="#2a6080" />
            <Pill label="Multi-month Horizon" color="#9a9590" />
          </div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 400, color: '#1a1c20', lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
            The Unpaid Layer:<br />
            <span style={{ color: '#8b6914' }}>Liquid Cooling After Power and Wires</span>
          </h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', color: '#9a9590', margin: '1rem 0 0', fontStyle: 'italic' }}>
            Three names for the thermal constraint the tape still treats as optional
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', backgroundColor: '#dedad4', marginTop: '2rem', border: '1px solid #dedad4' }}>
            {[
              { label: 'Coverage', value: 'MOD · AAON · ECL' },
              { label: 'Audience', value: 'Multi-month / Long-term Allocators' },
              { label: 'As of', value: REPORT_META.date },
            ].map((item, i) => (
              <div key={i} style={{ backgroundColor: '#faf8f5', padding: '1rem 1.25rem' }}>
                <div style={{ marginBottom: '4px' }}><Label>{item.label}</Label></div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: '#2a2c30' }}>{item.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1.25rem' }}>
            <Label>Related Research</Label>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {REPORT_META.companions.map((c, i) => (
                <span key={i} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#b8b3ac' }}>
                  <span style={{ color: '#9a9590' }}>↗</span> {c.label} <span style={{ color: '#c8c3bc' }}>({c.date})</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── verdict ── */}
        <div style={{ backgroundColor: '#faf8f5', border: '1px solid #dedad4', borderLeft: '3px solid #8b6914', padding: '1.5rem', marginBottom: '3rem' }}>
          <div style={{ marginBottom: '0.75rem' }}><Label>Verdict — Summary</Label></div>
          <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.15rem', lineHeight: 1.6, color: '#2a2c30', margin: 0, fontWeight: 400 }}>
            The market has already priced "AI pays," crowded the electrons story (CEG/VST/VRT), and started to discover electrical last mile. It has <span style={{ color: '#8b6914', fontStyle: 'italic' }}>not</span> finished pricing the next physical gate: <span style={{ color: '#8b6914' }}>heat</span>. As racks move past air's practical ceiling toward 100kW+ and eventually MW-class designs, liquid cooling stops being facility overhead and becomes capacity itself. August 7 made the orphaning obvious — while Industrials printed +0.23%. The overlooked book is not Vertiv. It is <strong style={{ color: '#1a1c20', fontWeight: 400 }}>MOD</strong> (OEM capacity locked by hyperscale LTA), <strong style={{ color: '#1a1c20', fontWeight: 400 }}>AAON</strong> (custom BASX air→liquid share gains), and <strong style={{ color: '#1a1c20', fontWeight: 400 }}>ECL</strong> (CoolIT closed + chemistry attach). History says long returns accrue to holders who buy <em>after</em> demand is proven and <em>during</em> digestion — which is exactly where MOD and AAON sit on the chart.
          </p>
        </div>

        {/* ── section 1: causal chain ── */}
        <SectionTitle number="01" title="The Causal Chain — Why This Industry, Why Now" />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          <div>
            <div style={{ backgroundColor: '#faf8f5', border: '1px solid #dedad4', padding: '1.5rem', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', lineHeight: 2, color: '#6a6d75' }}>
              {[
                ['AI Demand Receipts', '(MSFT / AMZN / PLTR)', '#8b6914'],
                ['Power Scarcity', '(AMZN: double capacity by 2027)', '#a07830'],
                ['Electrical Last Mile', '(POWL → MYRG → ENS)', '#b08840'],
                ['Dense Racks Break Air', '→ LIQUID / THERMAL ◄ unpaid layer', '#2a6080'],
                ['MOD → AAON → ECL', '(equipment / campus / loop+service)', '#2a6080'],
              ].map(([label, sub, color], i, arr) => (
                <div key={i}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline' }}>
                    <span style={{ color: color as string, fontSize: '13px' }}>{label}</span>
                    <span style={{ color: '#c8c3bc', fontSize: '10px' }}>{sub}</span>
                  </div>
                  {i < arr.length - 1 && <div style={{ color: '#dedad4', paddingLeft: '4px' }}>│</div>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p style={{ color: '#5a5c62', fontSize: '13.5px', lineHeight: 1.7, margin: '0 0 1rem' }}>
              Wall Street prices the first-order effect quickly. It is slower on everything that follows. Oil relief, dual megacap receipts, and CAT's AI data-center guidance already funded that sequence through early August.
            </p>
            <p style={{ color: '#5a5c62', fontSize: '13.5px', lineHeight: 1.7, margin: 0 }}>
              August 7 <span style={{ color: '#1a1c20' }}>interrupted</span> it with a rate-relief software bid — which is useful. Interruptions are when second-order sleeves get left behind.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '0.5rem' }}><Label>August 7 — Structured Read</Label></div>
        <Table
          headers={['Signal', 'Print', 'Implication for Thermal']}
          rows={MACRO_TABLE.map(r => [r.signal, r.print, r.implication])}
          highlightRow={i => i === 2}
        />

        {/* ── section 2: why not vertiv ── */}
        <SectionTitle number="02" title="Why Not Vertiv — And What That Buys You" />
        <Table
          headers={['Crowded Expression', 'This Book']}
          rows={[
            ['Vertiv full-stack thermal mindshare', 'MOD — Airedale OEM + contracted capacity (hyperscale LTA)'],
            ['Mega HVAC / electrical conglomerates', 'AAON — BASX custom DC thermal, campus-scale'],
            ['"Buy water utilities" vagueness', 'ECL — CoolIT hardware + fluid chemistry + field service'],
          ]}
        />
        <p style={{ color: '#9a9590', fontSize: '13px', lineHeight: 1.6, marginTop: '-0.5rem', marginBottom: '2rem', fontStyle: 'italic' }}>
          Same economic exposure. Cleaner remaining asymmetry after Jul 24–29's correlated wash.
        </p>

        {/* ── section 3: wash that created the entry ── */}
        <SectionTitle number="03" title="The Wash That Created the Entry" />
        <p style={{ color: '#5a5c62', fontSize: '13.5px', lineHeight: 1.7, marginBottom: '1rem' }}>
          Jul 24 → Jul 29 sold thermal like semi beta. SPY barely moved.
        </p>
        <Table
          headers={['Name', 'Jul 24→29', 'Still vs Jul 24 (Aug 7)', 'Excess vs SPY', 'vs June Peak']}
          rows={WASH_TABLE.map(r => [r.name, r.wash, r.vsJul24, r.excessSpy, r.vsJunePeak])}
          highlightRow={i => WASH_TABLE[i].highlight}
        />
        <div style={{ backgroundColor: 'rgba(139,105,20,0.06)', border: '1px solid rgba(139,105,20,0.2)', padding: '1rem 1.25rem', marginBottom: '2rem' }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#8b6914' }}>MOD climax volume Jul 29: 4.8M shares.</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#9a9590', marginLeft: '0.75rem' }}>That was capitulation, not a demand revision. The bounce since is unfinished — which is the point.</span>
        </div>

        {/* ── section 4: historical analogs ── */}
        <SectionTitle number="04" title="Historical Lens — Archive Clocks + Forward Outlook" />
        <p style={{ color: '#9a9590', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6, marginBottom: '1rem' }}>
          Massive EOD excess vs SPY on the same episodes used in Aug 4–5 notes:
        </p>
        <Table
          headers={['Episode', 'MOD', 'AAON', 'ECL', 'VRT']}
          rows={ANALOG_TABLE.map(r => [r.episode, r.mod, r.aaon, r.ecl, r.vrt])}
        />
        <div style={{ backgroundColor: '#faf8f5', border: '1px solid #dedad4', padding: '1rem 1.25rem', marginBottom: '2rem' }}>
          <Label>Read</Label>
          <p style={{ color: '#5a5c62', fontSize: '13px', lineHeight: 1.6, margin: '0.5rem 0 0' }}>
            On oil-relief and AI-receipt clocks, <span style={{ color: '#8b6914' }}>MOD and AAON often paid before or without VRT</span> on the first 10–20 sessions. Forward Outlook twins to August 7 are coin-flip for next-day SPY — and still <span style={{ color: '#2e7d4f' }}>positive excess at +20d for all three with 75% hit</span>. Horizon is multi-month, not Monday morning.
          </p>
        </div>

        {/* ── section 5: technicals ── */}
        <SectionTitle number="05" title="Technical State — Honest, Not Decorative" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', backgroundColor: '#dedad4', border: '1px solid #dedad4', marginBottom: '1.5rem' }}>
          {NAMES.map(n => (
            <div key={n.ticker} style={{ backgroundColor: '#faf8f5', padding: '1.25rem' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '16px', color: n.color, fontWeight: 600, marginBottom: '4px' }}>{n.ticker}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', color: '#2a2c30', marginBottom: '1rem' }}>{n.price}</div>
              {[
                { k: 'RSI (14)', v: String(n.rsi), alert: n.rsi < 40 },
                { k: 'vs SMA20', v: n.vsSma, alert: n.vsSma.startsWith('−') },
                { k: 'MACD Hist', v: n.macd, alert: false },
                { k: 'Structure', v: n.structure, alert: false },
              ].map(({ k, v, alert }) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #edeae5' }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#b8b3ac', textTransform: 'uppercase' }}>{k}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: alert ? '#a07000' : '#6a6d75' }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '9px', color: '#b84038', textTransform: 'uppercase', marginBottom: '3px' }}>Stop</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#b84038' }}>{n.stop}</div>
              </div>
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '9px', color: '#2e7d4f', textTransform: 'uppercase', marginBottom: '3px' }}>Confirm</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#2e7d4f' }}>{n.confirm}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ backgroundColor: '#faf8f5', border: '1px solid #dedad4', borderLeft: '3px solid #2a6080', padding: '1rem 1.25rem', marginBottom: '2rem' }}>
          <Label>Decisive Read</Label>
          <p style={{ color: '#5a5c62', fontSize: '13px', lineHeight: 1.6, margin: '0.5rem 0 0' }}>
            MOD / AAON are <span style={{ color: '#8b6914' }}>repair setups after capitulation</span>, not confirmed breakouts. ECL is already a bullish structure. That split matches the fundamental roles — torque / growth / ballast — and tells you how to size: accumulate bases, don't chase phantom breakouts.
          </p>
        </div>

        {/* ── section 6: the three names ── */}
        <SectionTitle number="06" title="The Three Names — Company Fuel, Tendency, Rhyme" />
        <p style={{ color: '#9a9590', fontSize: '12px', marginBottom: '1rem', fontFamily: "'JetBrains Mono', monospace" }}>Click any row to expand company detail →</p>
        {NAMES.map(n => <NameCard key={n.ticker} {...n} />)}

        {/* ── section 7: synthesis ── */}
        <SectionTitle number="07" title="How the Pieces Synthesize" />
        <Table
          headers={['Layer', 'Finding', 'Decision']}
          rows={SYNTHESIS_TABLE.map(r => [r.layer, r.finding, r.decision])}
        />

        <div style={{ marginBottom: '0.75rem' }}><Label>Book Construction</Label></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', backgroundColor: '#dedad4', border: '1px solid #dedad4', marginBottom: '2rem' }}>
          {BOOK_TABLE.map((b, i) => (
            <div key={i} style={{ backgroundColor: '#faf8f5', padding: '1.25rem' }}>
              <div style={{ marginBottom: '0.5rem' }}><Pill label={b.role} color={NAMES[i].color} /></div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '16px', color: NAMES[i].color, fontWeight: 600, marginBottom: '0.75rem' }}>{b.name}</div>
              {[
                { k: 'Weight', v: b.weight },
                { k: 'Add when', v: b.addWhen },
                { k: 'Stop', v: b.stop },
              ].map(({ k, v }) => (
                <div key={k} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '9px', color: '#b8b3ac', textTransform: 'uppercase', marginBottom: '2px' }}>{k}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#6a6d75' }}>{v}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ backgroundColor: 'rgba(42,96,128,0.05)', border: '1px solid rgba(42,96,128,0.2)', padding: '1rem 1.25rem', marginBottom: '2rem' }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#2a6080' }}>Do not substitute VRT as the alpha source. Optional control only.</span>
        </div>

        {/* ── section 8: kill list ── */}
        <SectionTitle number="08" title="What Would Kill This" />
        <div style={{ border: '1px solid #dedad4', backgroundColor: '#faf8f5' }}>
          {KILL_LIST.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', padding: '0.9rem 1.25rem', borderBottom: i < KILL_LIST.length - 1 ? '1px solid #edeae5' : 'none' }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#b84038', minWidth: '1.5rem', marginTop: '2px' }}>{String(i + 1).padStart(2, '0')}</span>
              <span style={{ color: '#5a5c62', fontSize: '13.5px', lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}
        </div>

        {/* ── bottom line ── */}
        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '2px solid #8b6914' }}>
          <div style={{ marginBottom: '1rem' }}><Label>Bottom Line</Label></div>
          <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.2rem', lineHeight: 1.65, color: '#2a2c30', margin: 0, fontWeight: 400 }}>
            August 7 paid narratives. The physical stack paid receipts for weeks and then got left behind. Liquid cooling is the unpaid layer after power and wires. <span style={{ color: '#8b6914' }}>MOD, AAON, and ECL</span> are already behaving like long-term winners — contracted capacity, record backlog, CoolIT closed. The tape's disagreement is concentrated in MOD/AAON's post-receipt digestion, which is historically where Vertiv-style multi-year fortunes were built. Buy the orphaned thermal sleeve as a <span style={{ color: '#8b6914' }}>base-building multi-month book</span>: seed torque and discovery into the wash repair, hold Ecolab as the Nalco-style attach compounder, and let confirmation print at the moving averages — not at the next payroll headline.
          </p>
        </div>

        {/* ── footer ── */}
        <div style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #dedad4' }}>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#c8c3bc', lineHeight: 1.7, margin: 0 }}>
            Data: Massive grouped EOD; Polygon daily through 2026-08-07; company IR (Modine FY26/LTA, AAON Q1'26, Ecolab CoolIT close); Market_Analyses/daily 2026-08-03…08-07. Not investment advice. For qualified institutional use only.
          </p>
        </div>

      </div>
    </div>
  )
}
